import React, { useCallback, useState } from 'react'
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Card, Flex, Spinner, Text } from "@radix-ui/themes";
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import { DragAndDropFileUpload } from '../../components/inputs/FileUpload/DragAndDropFileUpload';
import { EnterIcon } from "@radix-ui/react-icons";
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { debounce, set } from 'lodash';
import { getStudentByCode, getStudentById, getStudentCoursesByStudentId } from '../../services/school/student/getStudentByCode';
import { getUserById } from '../../services/user/getUsers';
import { resetStudentData, setStudentData } from '../../redux/slices/school/student';
import { resetUserData, setUserData } from '../../redux/slices/users/user';
import { UserPreview } from '../Users/UserPreview';
import { useMutation, useQuery } from 'react-query';
import { getFile } from '../../utils/fileUtils';
import { createStudentCertificate } from '../../services/school/student/createStudent';
import toast from 'react-hot-toast';
import { ConfirmAlertDialog } from '../../components/common/ConfirmAlertDialog';
import { CourseType } from '../../types/Course';
import { DocumentTypeEnum } from '../../types/Student';
import { removeDiacritics } from '../../utils/stringUtils';

const uploadStudentCertificateFormValidation = yup.object().shape({
  studentCode: yup.string().required("O código do estudante é obrigatório"),
  file: yup
    .mixed<File>()
    .required("O ficheiro é obrigatório")
    .test("fileFormat", "Apenas ficheiros .pdf são permitidos", (value) =>
      value instanceof File ? value.name.toLowerCase().endsWith(".pdf") : false
    ),
  type: yup.string().required("O tipo é obrigatório"),
  areYouSure: yup.boolean().default(false),
});

export const UploadStudentCertificate = () => {
  const dispatch = useAppDispatch();
  const userData = useAppSelector(state => state.user);
  const [courses, setCourses] = useState<CourseType[]>([]);
  const mutation = useMutation(createStudentCertificate);
  const {
    reset,
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<
    {
      file: File,
      studentCode: string,
      type: string,
      areYouSure: boolean,
      confirmIt?: boolean,
      certificateBase64?: string,
      studentId?: string,
      idStudent?: string,
      courseId?: string,
    }>({
      resolver: yupResolver(uploadStudentCertificateFormValidation),
      mode: "onBlur",
      reValidateMode: "onBlur",
    });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const course = courses.find(c => c.id === data.courseId);
      data.type = `${removeDiacritics(data.type.toUpperCase())}_${removeDiacritics((course?.level ?? '').toUpperCase())}`;
      await mutation.mutateAsync(data);
      reset();
      dispatch(resetStudentData());
      dispatch(resetUserData());
      toast.success("Documento carregado!");
    } catch (error: any) {
      if (error?.message.includes("já existe o certificado, deseja substituir?")) {
        setValue('confirmIt', true);
        return;
      }
      toast.error(`Erro: ${error.message}`);

    }
  });

  const { isLoading: isLoadingGetStudent, refetch: fetchStudentByCode, isError: isErrorGetStudent, error } = useQuery(
    ['getStudentByCode'],
    async () => {
      const studentCodeValue = getValues('studentCode');
      try {
        const studentData = await getStudentByCode(studentCodeValue);
        setValue('studentId', studentData.id);
        if (!studentData) return;
        const userData = await getUserById(studentData.idUser)
        if (!userData) return;
        const courses = await getStudentCoursesByStudentId(studentData.id!);
        setCourses(courses);
        dispatch(setStudentData(studentData));
        dispatch(setUserData(userData));
        setValue('idStudent', studentData.id!);
      } catch (error) {
        setCourses([]);
        dispatch(resetStudentData());
        dispatch(resetUserData());
        setValue('studentId', '');
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const handleFileOnChange = async (file: File | null) => {
    if (file) {
      const { base64 } = await getFile(file!);
      setValue('certificateBase64', base64 as string);
      return;
    }
    setValue('certificateBase64', '');
  }
  const handleStudentCodeChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue('studentCode', trimmedValue);
      if (trimmedValue.length >= 5) {
        fetchStudentByCode();
      } else {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    }, 200),
    [setValue, fetchStudentByCode]
  );
  function handleChangeCourse(value: string): void {
    setValue('courseId', value);
  }

  function handleChangeDocType(value: string): void {
    setValue('type', value);
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size="1">
        <Flex direction="column" gap="4">
          <TextFieldComponent
            name="studentCode"
            label="Código do estudante"
            control={control}
            errors={errors}
            fieldProps={{
              placeholder: 'Digite o código do estudante',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                handleStudentCodeChange(pastedText);
              }
            }}
            required={true}
          />
          {userData && userData?.id && <>
            <UserPreview />
          </>}
          <SelectComponent
            name="courseId"
            label="Cursos"
            control={control}
            errors={errors}
            required={true}
            options={courses.map((course) => ({
              value: course.id,
              label: course.name
            }))}
            onSelect={handleChangeCourse}
          />
          <SelectComponent
            name="type"
            label="Tipo de documento que carrega"
            control={control}
            errors={errors}
            options={[
              { value: DocumentTypeEnum.DECLARACAO, label: "Declaração" },
              { value: DocumentTypeEnum.CERTIFICADO, label: "Certificado" },
              { value: DocumentTypeEnum.DIPLOMA, label: "Diploma" },
              { value: DocumentTypeEnum.CREDENCIAL, label: "Credencial" },
            ]}
            required={true}
            onSelect={handleChangeDocType}
          />
          <Box>
            <Text as="label" size="2"><span>Documento digitalizado <Text color="red">*</Text></span></Text>
            <Controller
              name="file"
              control={control}
              render={({ field }) => (
                <DragAndDropFileUpload
                  onFileDrop={(file) => {
                    field.onChange(file);
                    handleFileOnChange(file);
                  }}
                  label="Arraste ou selecione o ficheiro"
                  accept="application/pdf"
                  selectedFile={field.value}
                  required={true}
                />
              )}
            />

            {errors.file && (
              <Text color="red" size="1">{`${errors.file.message}`}</Text>
            )}
          </Box>
          <ConfirmAlertDialog
            open={!!watch('confirmIt')}
            onOpenChange={(open) => !open && setValue('confirmIt', false)}
            title="Deseja realmente substituir o documento existente?"
            description="O documento já carregado será substituído por este novo ficheiro."
            confirmLabel="Substituir"
            item={watch('confirmIt')}
            onConfirm={() => {
              setValue('areYouSure', true);
              onSubmit();
            }}
            onCancel={() => {
              setValue('areYouSure', false)
              setValue('confirmIt', false)
            }}
          />
          <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
            <Button type="submit" disabled={mutation.isLoading}>
              {mutation.isLoading ? (
                <Spinner loading>
                  <EnterIcon />
                </Spinner>
              ) : (
                <EnterIcon />
              )}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}

